import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Lightbulb, Sparkles, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";

const STEPS = [
  {
    icon: Lightbulb,
    title: "Share and discover ideas",
    body: "Post an actionable idea or upvote what the community is rallying behind.",
  },
  {
    icon: Sparkles,
    title: "Run the Coach",
    body: "Get a positioning summary plus matched grants, partners, and legislation.",
  },
  {
    icon: User,
    title: "Track it all in your Profile",
    body: "Everything you start — ideas, saved grants, comments — lives here.",
  },
];

const WelcomeTour = () => {
  const { user } = useAuth();
  const { row, tourCompleted, loading, needsWelcome, refetch } = useOnboardingStatus();
  const location = useLocation();
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (loading || !user) return;
    if (needsWelcome) return; // they haven't finished /welcome yet
    if (tourCompleted) return;
    // Only show on app pages — not /welcome itself.
    if (location.pathname.startsWith("/welcome")) return;
    setOpen(true);
  }, [loading, user, needsWelcome, tourCompleted, location.pathname]);

  const finish = async () => {
    setOpen(false);
    if (!user) return;
    await supabase
      .from("user_onboarding")
      .update({ tour_completed_at: new Date().toISOString() })
      .eq("user_id", user.id);
    await refetch();
  };

  if (!open || !row) return null;

  const { icon: Icon, title, body } = STEPS[step];
  const isLast = step === STEPS.length - 1;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm w-[calc(100%-2rem)] sm:w-80">
      <div className="bg-card border border-border rounded-2xl shadow-card p-5 relative">
        <button
          onClick={finish}
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-primary mb-3 font-semibold">
          <Icon className="h-4 w-4" /> Quick tour · {step + 1}/{STEPS.length}
        </div>
        <h3 className="font-display font-semibold mb-1">{title}</h3>
        <p className="text-sm text-muted-foreground mb-4">{body}</p>
        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 w-5 rounded-full ${i <= step ? "bg-primary" : "bg-muted"}`}
              />
            ))}
          </div>
          <button
            onClick={isLast ? finish : () => setStep((s) => s + 1)}
            className="text-sm font-medium text-primary hover:underline"
          >
            {isLast ? "Got it" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeTour;